import { useMemo, useState } from 'react';
import {
  Box, Button, FormControl, IconButton, InputLabel, MenuItem, Select, Stack, TextField, Typography,
} from '@mui/material';
import { AddOutlined, DeleteOutline } from '@mui/icons-material';
import { IEmissionRuleInput } from '../../../../shared/services/survey/survey.model';
import {
  LINEAR_OPS,
  LinearFormula,
  LinearFormulaStep,
  LinearOp,
  extractAliasRefs,
  linearToTree,
  traceFormula,
} from '../../../../shared/utils/survey-rules/formula';

interface Props {
  formula: LinearFormula;
  onChange: (f: LinearFormula) => void;
  /** The rule's explicit inputs; used for alias choices and the example-values preview. */
  inputs: IEmissionRuleInput[];
  /** Value names from intermediate calculations, available as extra inputs. */
  adjustmentAliases?: string[];
  disabled?: boolean;
}

/**
 * Step-by-step formula editor with a live "try with example values" preview.
 * Owns the example values internally; the caller only holds the formula.
 */
export const LinearFormulaEditor = ({ formula, onChange, inputs, adjustmentAliases = [], disabled = false }: Props) => {
  const [previewValues, setPreviewValues] = useState<Record<string, string>>({});

  const aliases = [
    ...new Set(inputs.map(i => i.alias).filter((a): a is string => typeof a === 'string' && a.trim() !== '')),
    ...adjustmentAliases,
  ];

  const update = (patch: Partial<LinearFormula>) => onChange({ ...formula, ...patch });
  const updateStep = (i: number, patch: Partial<LinearFormulaStep>) =>
    update({ steps: formula.steps.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const addStep = () =>
    update({ steps: [...formula.steps, { op: '×', valueKind: 'alias', alias: aliases[0] ?? '', literal: undefined }] });
  const removeStep = (i: number) => update({ steps: formula.steps.filter((_, j) => j !== i) });

  const formulaTree = useMemo(() => linearToTree(formula), [formula]);

  // Augment the explicit inputs with synthetic adjustmentRef entries for any adjustment
  // aliases referenced in the formula, so the preview shows them even before save.
  const previewInputs = useMemo(() => {
    const existingAliases = new Set(inputs.map(i => i.alias));
    const adjRefs = [...new Set(
      extractAliasRefs(formulaTree).filter(a => adjustmentAliases.includes(a) && !existingAliases.has(a)),
    )].map(alias => ({ alias, kind: 'adjustmentRef' as const } as IEmissionRuleInput));
    return [...inputs, ...adjRefs];
  }, [inputs, formulaTree, adjustmentAliases]);

  const previewBindings = useMemo((): Record<string, number | null> => {
    const result: Record<string, number | null> = {};
    for (const inp of previewInputs) {
      if (!inp.alias) continue;
      if (inp.kind === 'literal') {
        result[inp.alias] = typeof inp.literalValue === 'number' ? inp.literalValue : null;
      } else if (inp.kind === 'date') {
        const ms = Date.parse(inp.dateValue ?? '');
        result[inp.alias] = isNaN(ms) ? null : Math.floor(ms / 86400000);
      } else {
        const raw = (previewValues[inp.alias] ?? '').trim();
        if (!raw) { result[inp.alias] = null; continue; }
        if (inp.path === 'textValueAsDate') {
          const ms = Date.parse(raw);
          result[inp.alias] = isNaN(ms) ? null : Math.floor(ms / 86400000);
        } else {
          const n = Number(raw);
          result[inp.alias] = Number.isFinite(n) ? n : null;
        }
      }
    }
    return result;
  }, [previewInputs, previewValues]);

  const previewTrace = useMemo((): Array<{ label: string; result: number | null }> => {
    if (Object.keys(previewBindings).length === 0) return [];
    try { return traceFormula(formulaTree, previewBindings); }
    catch { return []; }
  }, [formulaTree, previewBindings]);

  const previewResult = previewTrace.length > 0
    ? previewTrace[previewTrace.length - 1].result
    : null;

  const valueControl = (
    kind: 'alias' | 'literal',
    aliasVal: string,
    literalVal: number | undefined,
    onKindChange: (k: 'alias' | 'literal') => void,
    onAliasChange: (a: string) => void,
    onLiteralChange: (n: number | undefined) => void,
  ) => (
    <>
      <FormControl size='small' sx={{ width: 90 }} disabled={disabled}>
        <Select value={kind} onChange={e => onKindChange(e.target.value as 'alias' | 'literal')}>
          <MenuItem value='alias'>Input</MenuItem>
          <MenuItem value='literal'>Number</MenuItem>
        </Select>
      </FormControl>
      {kind === 'alias' ? (
        <FormControl size='small' sx={{ minWidth: 130 }} disabled={disabled}>
          <InputLabel>Input</InputLabel>
          <Select label='Input' value={aliasVal} onChange={e => onAliasChange(e.target.value)}>
            {aliases.length === 0
              ? <MenuItem value='' disabled>(no inputs defined)</MenuItem>
              : aliases.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </Select>
        </FormControl>
      ) : (
        <TextField
          size='small'
          label='Value'
          type='number'
          disabled={disabled}
          value={literalVal ?? ''}
          onChange={e => onLiteralChange(e.target.value === '' ? undefined : Number(e.target.value))}
          sx={{ width: 120 }}
          inputProps={{ step: 'any' }}
        />
      )}
    </>
  );

  return (
    <Stack spacing={1.5}>
      <Stack direction='row' spacing={1} alignItems='center'>
        <Typography variant='caption' color='text.secondary' sx={{ minWidth: 72 }}>Start with</Typography>
        {valueControl(
          formula.startKind, formula.startAlias, formula.startLiteral,
          k => update({ startKind: k }),
          a => update({ startAlias: a }),
          n => update({ startLiteral: n }),
        )}
      </Stack>

      {formula.steps.map((step, i) => (
        <Stack key={i} direction='row' spacing={1} alignItems='center'>
          <Typography variant='caption' color='text.secondary' sx={{ minWidth: 72 }}>Step {i + 1}</Typography>
          <FormControl size='small' sx={{ width: 140 }} disabled={disabled}>
            <InputLabel>Operation</InputLabel>
            <Select label='Operation' value={step.op} onChange={e => updateStep(i, { op: e.target.value as LinearOp })}>
              {LINEAR_OPS.map(op => <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>)}
            </Select>
          </FormControl>
          {valueControl(
            step.valueKind, step.alias, step.literal,
            k => updateStep(i, { valueKind: k }),
            a => updateStep(i, { alias: a }),
            n => updateStep(i, { literal: n }),
          )}
          <IconButton size='small' onClick={() => removeStep(i)} disabled={disabled}>
            <DeleteOutline fontSize='small' />
          </IconButton>
        </Stack>
      ))}

      <Button size='small' startIcon={<AddOutlined />} onClick={addStep} disabled={disabled} sx={{ alignSelf: 'flex-start' }}>
        Add step
      </Button>

      {previewInputs.length > 0 && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
          <Typography variant='caption' color='text.secondary' sx={{ mb: 1.5, display: 'block' }}>
            Try with example values
          </Typography>
          <Stack spacing={1} sx={{ mb: 2 }}>
            {previewInputs.map((inp, idx) => {
              if (inp.kind === 'literal') {
                return (
                  <Stack key={idx} direction='row' spacing={1} alignItems='center'>
                    <Typography variant='body2' sx={{ minWidth: 90 }}><strong>{inp.alias}</strong></Typography>
                    <Typography variant='body2' color='text.secondary'>constant = {inp.literalValue}</Typography>
                  </Stack>
                );
              }
              if (inp.kind === 'date') {
                const days = previewBindings[inp.alias];
                return (
                  <Stack key={idx} direction='row' spacing={1} alignItems='center'>
                    <Typography variant='body2' sx={{ minWidth: 90 }}><strong>{inp.alias}</strong></Typography>
                    <Typography variant='body2' color='text.secondary'>
                      date = {inp.dateValue || '—'}{days != null ? ` (counted as ${days} days in calculations)` : ''}
                    </Typography>
                  </Stack>
                );
              }
              if (inp.kind === 'adjustmentRef') {
                return (
                  <Stack key={idx} direction='row' spacing={1} alignItems='center'>
                    <Typography variant='body2' sx={{ minWidth: 90 }}><strong>{inp.alias}</strong></Typography>
                    <TextField
                      size='small'
                      label='Example value'
                      type='number'
                      value={previewValues[inp.alias] ?? ''}
                      onChange={e => setPreviewValues(prev => ({ ...prev, [inp.alias]: e.target.value }))}
                      sx={{ width: 130 }}
                      inputProps={{ step: 'any' }}
                    />
                    <Typography variant='caption' color='text.secondary'>from intermediate calculation</Typography>
                  </Stack>
                );
              }
              const isDatePath = inp.path === 'textValueAsDate';
              const resolvedVal = previewBindings[inp.alias];
              return (
                <Stack key={idx} direction='row' spacing={1} alignItems='center'>
                  <Typography variant='body2' sx={{ minWidth: 90 }}><strong>{inp.alias}</strong></Typography>
                  <TextField
                    size='small'
                    label={isDatePath ? 'Example date' : 'Example value'}
                    type={isDatePath ? 'date' : 'number'}
                    value={previewValues[inp.alias] ?? ''}
                    onChange={e => setPreviewValues(prev => ({ ...prev, [inp.alias]: e.target.value }))}
                    sx={{ width: isDatePath ? 180 : 130 }}
                    InputLabelProps={isDatePath ? { shrink: true } : undefined}
                    inputProps={isDatePath ? undefined : { step: 'any' }}
                  />
                  {isDatePath && resolvedVal != null && (
                    <Typography variant='caption' color='text.secondary'>counted as {resolvedVal} days</Typography>
                  )}
                </Stack>
              );
            })}
          </Stack>
          {previewTrace.length > 1 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant='caption' color='text.secondary' sx={{ mb: 0.5, display: 'block' }}>
                Computation steps
              </Typography>
              <Stack spacing={0.5}>
                {previewTrace.map((step, i) => (
                  <Stack key={i} direction='row' spacing={1} alignItems='center'>
                    <Typography variant='caption' color='text.disabled' sx={{ minWidth: 18 }}>
                      {i + 1}.
                    </Typography>
                    <Typography variant='body2' sx={{ flex: 1, fontFamily: 'monospace', fontSize: '0.825rem' }}>
                      {step.label}
                    </Typography>
                    <Typography variant='body2' color='text.secondary' sx={{ mx: 0.5 }}>→</Typography>
                    <Typography
                      variant='body2'
                      fontWeight='medium'
                      color={step.result != null ? 'text.primary' : 'text.disabled'}
                      sx={{ minWidth: 60, textAlign: 'right' }}
                    >
                      {step.result != null
                        ? step.result.toLocaleString(undefined, { maximumFractionDigits: 6 })
                        : '—'}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant='body2' color='text.secondary'>Formula result:</Typography>
            <Typography variant='body1' fontWeight='bold' color={previewResult != null ? 'text.primary' : 'text.disabled'}>
              {previewResult != null
                ? previewResult.toLocaleString(undefined, { maximumFractionDigits: 6 })
                : '—'}
            </Typography>
          </Box>
        </Box>
      )}
    </Stack>
  );
};
